/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 인라인 서식을 처리하는 클래스를 정의한다.
 *
 * @file /src/rootin/selection/Mark.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 19.
 */
import { Editor } from '@rootin/core/Editor';
import { Element } from '@rootin/base/Element';

export class Mark {
    /**
     * 에디터의 인라인 서식을 처리하는 클래스를 정의한다.
     *
     * @param editor - 인라인 서식을 적용할 클래스
     */
    constructor(public editor: Editor) {}

    /**
     * 선택영역(또는 지정한 Range) 시작 지점을 포함하는 가장 가까운 마크 조상 요소를 찾는다.
     *
     * @param selector - 확인할 셀렉터 (예: 'b, strong')
     * @param range - 확인할 Range (미지정 시 `editor.selection.getRange()`)
     * @returns 매칭되는 조상 요소 (없으면 null)
     */
    closest(selector: string, range: Range | null = null): HTMLElement | null {
        range ??= this.editor.selection.getRange();
        if (range === null) {
            return null;
        }

        const container = range.startContainer;
        const node =
            container.nodeType === Node.ELEMENT_NODE
                ? ((container as HTMLElement).childNodes[range.startOffset] ?? container)
                : container;

        const target = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
        if (target === null) {
            return null;
        }

        const matched = target.closest(selector);
        if (matched === null) {
            return null;
        }

        if (this.editor.root.body.$getElement().getEl().contains(matched) === false) {
            return null;
        }

        return matched as HTMLElement;
    }

    /**
     * 엘리먼트에 attrs 로 지정한 속성을 설정한다(예: `{ style: 'color: var(--rootin-color-1);' }`).
     *
     * @param el - 속성을 설정할 엘리먼트
     * @param attrs - 설정할 속성 목록
     */
    #applyAttrs(el: HTMLElement, attrs?: { [key: string]: string }): void {
        if (attrs === undefined) {
            return;
        }

        for (const [key, value] of Object.entries(attrs)) {
            el.setAttribute(key, value);
        }
    }

    /**
     * 선택영역(또는 지정한 Range)을 지정한 태그로 감싼다.
     *
     * @param tagName - 감쌀 태그명 (예: 'b')
     * @param range - 감쌀 대상 Range (미지정 시 `editor.selection.getRange()`)
     * @param attrs - 감쌀 태그에 설정할 속성(예: 컬러 마크의 `style`)
     */
    wrap(tagName: string, range: Range | null = null, attrs?: { [key: string]: string }): void {
        range ??= this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const $wrap = document.createElement(tagName);
        this.#applyAttrs($wrap, attrs);
        $wrap.appendChild(range.extractContents());
        range.insertNode($wrap);

        this.editor.selection.select($wrap, $wrap);
        this.normalize(tagName);
    }

    /**
     * 선택영역(또는 지정한 Range) 내부의 selector 매칭 태그를 모두 벗겨낸다.
     *
     * @param selector - 벗겨낼 셀렉터 (예: 'b, strong')
     * @param range - 대상 Range (미지정 시 `editor.selection.getRange()`)
     */
    unwrap(selector: string, range: Range | null = null): void {
        range ??= this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const contents = range.extractContents();
        for (const $matched of Array.from(contents.querySelectorAll(selector))) {
            $matched.replaceWith(...Array.from($matched.childNodes));
        }

        const $first = contents.firstChild;
        const $last = contents.lastChild;

        this.splitOut(selector, range);
        range.insertNode(contents);

        for (const $empty of Array.from(this.editor.root.body.$getElement().getEl().querySelectorAll(selector))) {
            if ($empty.childNodes.length === 0) {
                $empty.remove();
            }
        }

        if ($first !== null && $last !== null) {
            this.editor.selection.select($first, $last);
        }

        this.normalize(selector);
    }

    /**
     * 선택영역에 마크를 토글한다. 선택영역이 없으면(캐럿만 있으면) 캐럿 위치에 빈 마크를 삽입한다.
     *
     * @param tag - 적용할 태그 (예: 'b')
     * @param selector - 판정/해제 셀렉터 (예: 'b, strong')
     * @param attrs - 적용할 태그에 설정할 속성(예: 컬러 마크의 `style`)
     */
    toggle(tag: string, selector: string, attrs?: { [key: string]: string }): void {
        const state = this.editor.selection.get();
        if (state.kind !== 'range') {
            return;
        }

        if (state.range.collapsed === true) {
            this.insertEmpty(tag, attrs);
            return;
        }

        const active = this.isActive(selector);
        const range = state.range;
        const targets = this.editor.selection
            .getElements()
            .filter((element) => element.kind === 'text' && range.intersectsNode(element.$getElement().getEl()));

        if (targets.length === 0) {
            return;
        }

        // 흔한 경우(한 엘리먼트 안에서만 선택) — 기존 wrap()/unwrap() 이 이미 selection.select()
        // 로 결과를 다시 선택해주는 걸 그대로 살린다.
        if (targets.length === 1) {
            const element = targets[0];
            element.onBeforeEdit();
            if (active === true) {
                this.unwrap(selector);
            } else {
                this.wrap(tag, undefined, attrs);
            }
            element.onEdit();
            return;
        }

        const start = this.editor.history.stack.length;
        for (const element of targets) {
            const clamped = this.#clampToElement(range, element);
            if (clamped.collapsed === true) {
                continue;
            }

            element.onBeforeEdit();
            if (active === true) {
                this.#unwrapWithin(selector, clamped, element);
            } else {
                this.#wrapWithin(tag, clamped, element, attrs);
            }
            element.onEdit();
        }

        const entries = this.editor.history.stack.splice(start);
        if (entries.length > 1) {
            this.editor.history.stack.push({ type: 'batch', entries });
        } else if (entries.length === 1) {
            this.editor.history.stack.push(entries[0]);
        }
    }

    /**
     * 선택영역 안의 마크를 활성여부와 무관하게 무조건 벗겨낸다(서식 지우기 — 컬러 마크의
     * `default` 항목처럼, 지금 무슨 색이 적용돼 있든 일단 지우고 싶을 때 쓴다). `toggle()`의
     * unwrap 분기와 동일하게 동작하되 active 판정을 건너뛴다. 선택영역이 없으면(캐럿만
     * 있으면) 지울 대상이 없으므로 아무 것도 하지 않는다.
     *
     * @param selector - 벗겨낼 셀렉터 (예: 'span[style^="color:"]')
     */
    clear(selector: string): void {
        const state = this.editor.selection.get();
        if (state.kind !== 'range' || state.range.collapsed === true) {
            return;
        }

        const range = state.range;
        const targets = this.editor.selection
            .getElements()
            .filter((element) => element.kind === 'text' && range.intersectsNode(element.$getElement().getEl()));

        if (targets.length === 0) {
            return;
        }

        if (targets.length === 1) {
            const element = targets[0];
            element.onBeforeEdit();
            this.unwrap(selector);
            element.onEdit();
            return;
        }

        const start = this.editor.history.stack.length;
        for (const element of targets) {
            const clamped = this.#clampToElement(range, element);
            if (clamped.collapsed === true) {
                continue;
            }

            element.onBeforeEdit();
            this.#unwrapWithin(selector, clamped, element);
            element.onEdit();
        }

        const entries = this.editor.history.stack.splice(start);
        if (entries.length > 1) {
            this.editor.history.stack.push({ type: 'batch', entries });
        } else if (entries.length === 1) {
            this.editor.history.stack.push(entries[0]);
        }
    }

    /**
     * 선택영역이 없을 때(캐럿만 있을 때) 캐럿 위치에 빈 마크 태그를 삽입하고 그 안으로 캐럿을 옮긴다.
     *
     * @param tag - 삽입할 태그명
     * @param attrs - 삽입할 태그에 설정할 속성(예: 컬러 마크의 `style`)
     */
    insertEmpty(tag: string, attrs?: { [key: string]: string }): void {
        const element = this.editor.focus.getElement();
        if (element === null || element.kind !== 'text') {
            return;
        }

        const range = this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        element.onBeforeEdit();

        // 완전히 빈 태그는 브라우저가 캐럿을 안에 붙잡아두지 못하고(타이핑하면 바깥으로
        // 새버림, 실측 확인) zero-width space 를 하나 넣어 캐럿이 실제로 붙잡을 콘텐츠를 준다.
        const $mark = document.createElement(tag);
        this.#applyAttrs($mark, attrs);
        const zeroWidth = document.createTextNode('​');
        $mark.appendChild(zeroWidth);
        range.insertNode($mark);

        const inner = document.createRange();
        inner.setStart(zeroWidth, 1);
        inner.setEnd(zeroWidth, 1);
        this.editor.selection.setRange(inner);

        element.onEdit();
    }

    /**
     * range를 element 의 경계 안으로 잘라낸(clamp) Range를 만든다.
     *
     * @param range - 원본 Range(여러 엘리먼트에 걸칠 수 있음)
     * @param element - 잘라낼 기준 엘리먼트
     * @returns element 안으로 잘라낸 Range
     */
    #clampToElement(range: Range, element: Element): Range {
        const el = element.$getElement().getEl();
        const clamped = document.createRange();

        if (el.contains(range.startContainer) === true) {
            clamped.setStart(range.startContainer, range.startOffset);
        } else {
            clamped.setStart(el, 0);
        }

        if (el.contains(range.endContainer) === true) {
            clamped.setEnd(range.endContainer, range.endOffset);
        } else {
            clamped.setEnd(el, el.childNodes.length);
        }

        return clamped;
    }

    /**
     * element 경계 안으로 잘라낸 range 를 태그로 감싼다(다중 엘리먼트 처리 중 하나 분량).
     *
     * @param tagName - 감쌀 태그명
     * @param range - element 안으로 이미 잘라낸 Range
     * @param element - 대상 엘리먼트
     * @param attrs - 감쌀 태그에 설정할 속성(예: 컬러 마크의 `style`)
     */
    #wrapWithin(tagName: string, range: Range, element: Element, attrs?: { [key: string]: string }): void {
        const $wrap = document.createElement(tagName);
        this.#applyAttrs($wrap, attrs);
        $wrap.appendChild(range.extractContents());
        range.insertNode($wrap);
        this.mergeMarks(element, tagName);
    }

    /**
     * element 경계 안으로 잘라낸 range 안의 selector 매칭 태그를 벗겨낸다.
     *
     * @param selector - 벗겨낼 셀렉터
     * @param range - element 안으로 이미 잘라낸 Range
     * @param element - 대상 엘리먼트
     */
    #unwrapWithin(selector: string, range: Range, element: Element): void {
        const contents = range.extractContents();
        for (const $matched of Array.from(contents.querySelectorAll(selector))) {
            $matched.replaceWith(...Array.from($matched.childNodes));
        }

        this.splitOut(selector, range);
        range.insertNode(contents);

        const el = element.$getElement().getEl();
        for (const $empty of Array.from(el.querySelectorAll(selector))) {
            if ($empty.childNodes.length === 0) {
                $empty.remove();
            }
        }

        this.mergeMarks(element, selector);
    }

    /**
     * 현재 커서위치가 매칭 조상 내부에 있으면, 그 조상 요소를 자르고 커서를 조상 바깥(뒤)으로 밀어낸다.
     *
     * @param selector - 분리할 조상 셀렉터 (예: 'b, strong')
     * @param range - 분리할 지점(collapsed 여부는 호출부 책임 — 이 메서드는 시작 경계만 본다)
     */
    splitOut(selector: string, range: Range): void {
        const $matched = this.closest(selector, range);
        if ($matched === null) {
            return;
        }

        if ($matched.lastChild !== null) {
            const tail = document.createRange();
            tail.setStart(range.startContainer, range.startOffset);
            tail.setEndAfter($matched.lastChild);

            if (tail.collapsed === false) {
                const $clone = $matched.cloneNode(false) as HTMLElement;
                $clone.appendChild(tail.extractContents());
                $matched.after($clone);
            }
        }

        range.setStartAfter($matched);
        range.collapse(true);
    }

    /**
     * 지금 포커스된 편집 유닛 안의 동일 마크를 병합하고 선택영역을 보존한다.
     *
     * @param selector - 정규화할 마크 셀렉터 (예: 'b, strong')
     */
    normalize(selector: string): void {
        const element = this.editor.focus.getElement();
        if (element === null) {
            return;
        }

        const offsets = this.editor.selection.getOffsets(element);
        this.mergeMarks(element, selector);
        if (offsets !== null) {
            this.editor.selection.setOffsets(element, offsets.start, offsets.end);
        }
    }

    /**
     * 엘리먼트 내 동일 마크를 중첩/인접 관계 없이 하나로 병합하고 빈 마크를 제거한다.
     *
     * @param element - 대상 편집 유닛
     * @param selector - 정규화할 마크 셀렉터
     */
    mergeMarks(element: Element, selector: string): void {
        const el = element.$getElement().getEl();
        el.normalize();

        for (const $empty of Array.from(el.querySelectorAll(selector))) {
            if ($empty.childNodes.length === 0) {
                $empty.remove();
            }
        }

        while (true) {
            let changed = false;
            for (const $matched of Array.from(el.querySelectorAll(selector))) {
                const $parent = $matched.parentElement;
                if (
                    $parent !== null &&
                    $parent !== el &&
                    $parent.matches(selector) &&
                    this.isMergeable($parent, $matched)
                ) {
                    $matched.replaceWith(...Array.from($matched.childNodes));
                    changed = true;
                    break;
                }
            }
            if (changed === false) {
                break;
            }
        }

        while (true) {
            let changed = false;
            for (const $matched of Array.from(el.querySelectorAll(selector))) {
                const $prev = $matched.previousSibling;
                if (
                    $prev !== null &&
                    $prev.nodeType === Node.ELEMENT_NODE &&
                    ($prev as HTMLElement).matches(selector) &&
                    this.isMergeable($prev as HTMLElement, $matched)
                ) {
                    while ($matched.firstChild !== null) {
                        ($prev as HTMLElement).appendChild($matched.firstChild);
                    }
                    $matched.remove();
                    changed = true;
                    break;
                }
            }
            if (changed === false) {
                break;
            }
        }

        el.normalize();
    }

    /**
     * 두 요소가 병합 가능한지 확인한다.
     *
     * @param a - 비교할 요소
     * @param b - 비교할 요소
     * @returns 병합 가능하면 true
     */
    isMergeable(a: globalThis.Element, b: globalThis.Element): boolean {
        if (a.tagName !== b.tagName) {
            return false;
        }
        if (a.attributes.length !== b.attributes.length) {
            return false;
        }
        for (const attr of Array.from(a.attributes)) {
            if (b.getAttribute(attr.name) !== attr.value) {
                return false;
            }
        }
        return true;
    }

    /**
     * 마크툴(Bold 등)을 표시할 수 있는 상태인지 확인한다.
     *
     * @param element - 커서가 위치한 편집유닛
     * @returns 노출하면 true
     */
    isVisible(element: Element | null): boolean {
        return element !== null;
    }

    /**
     * 현재 포커스된 위치 또는 선택영역이 특정 마크 안인지 판정한다.
     *
     * @param selector - 판정할 마크 셀렉터 (예: 'b, strong')
     * @returns 활성(마크 안)이면 true
     */
    isActive(selector: string): boolean {
        const state = this.editor.selection.get();
        return state.kind === 'range' && this.closest(selector, state.range) !== null;
    }
}
